import React, { useState, useEffect } from 'react';
import {
  Modal, Drawer, Button, Space, Typography, Divider, Tag, Descriptions, Alert, Timeline,
  Spin, Tooltip, Card, Row, Col, message
} from 'antd';
import {
  CalendarOutlined, UserOutlined, CarOutlined, ClockCircleOutlined,
  HistoryOutlined, EditOutlined, SwapOutlined, StopOutlined, DeleteOutlined,
  WarningOutlined, CheckCircleOutlined, InfoCircleOutlined, EnvironmentOutlined
} from '@ant-design/icons';
import { appointmentApi, Appointment } from '../../services/appointmentApi';
import { jobStatusColor } from '../Scheduling/statusColors';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

interface AppointmentDetailModalProps {
  visible: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onReschedule?: (appointment: Appointment) => void;
  onChangeTechnician?: (appointment: Appointment) => void;
  onCancel?: (appointment: Appointment) => void;
  onDelete?: (appointment: Appointment) => void;
}

interface AppointmentHistory {
  id: number;
  change_type: string;
  field_changed: string;
  old_value: string;
  new_value: string;
  changed_by_user_id: number;
  changed_by_name: string;
  reason: string;
  customer_notified: boolean;
  notification_sent: boolean;
  created_at: string;
}

export const AppointmentDetailModal: React.FC<AppointmentDetailModalProps> = ({
  visible,
  appointment,
  onClose,
  onReschedule,
  onChangeTechnician,
  onCancel,
  onDelete
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<AppointmentHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [fullAppointment, setFullAppointment] = useState<Appointment | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (visible && appointment) {
      loadAppointmentDetails();
      loadAppointmentHistory();
    } else if (!visible) {
      // Clear state when modal closes
      setFullAppointment(null);
      setHistory([]);
    }
  }, [visible, appointment]);

  const loadAppointmentDetails = async () => {
    if (!appointment?.id) return;

    setLoading(true);
    try {
      const details = await appointmentApi.getById(appointment.id);
      setFullAppointment(details);
    } catch (error: any) {
      console.error('Error loading appointment details:', error);
      // If 404, appointment no longer exists - just use the basic appointment data
      if (error?.response?.status === 404 || error?.status === 404) {
        console.warn('Appointment not found, using provided appointment data');
        setFullAppointment(appointment);
      } else {
        message.error('Failed to load appointment details');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAppointmentHistory = async () => {
    if (!appointment?.id) return;

    setLoadingHistory(true);
    try {
      const response = await appointmentApi.getHistory(appointment.id);
      setHistory(response.history || []);
    } catch (error: any) {
      console.error('Error loading appointment history:', error);
      // If 404 or error, just show empty history
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const getChangeTypeIcon = (changeType: string) => {
    switch (changeType) {
      case 'created': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'reschedule': return <CalendarOutlined style={{ color: '#1890ff' }} />;
      case 'tech_change': return <SwapOutlined style={{ color: '#722ed1' }} />;
      case 'status_change': return <InfoCircleOutlined style={{ color: '#fa8c16' }} />;
      case 'cancel': return <StopOutlined style={{ color: '#ff4d4f' }} />;
      case 'delete': return <DeleteOutlined style={{ color: '#8c8c8c' }} />;
      default: return <HistoryOutlined />;
    }
  };

  const getChangeTypeLabel = (changeType: string) => {
    switch (changeType) {
      case 'created': return 'Created';
      case 'reschedule': return 'Rescheduled';
      case 'tech_change': return 'Technician Changed';
      case 'status_change': return 'Status Changed';
      case 'update': return 'Updated';
      case 'cancel': return 'Cancelled';
      case 'delete': return 'Deleted';
      default: return changeType;
    }
  };

  const renderHistory = () => {
    if (loadingHistory) {
      return <Spin tip="Loading history..." />;
    }

    if (history.length === 0) {
      return <Text type="secondary">No history available</Text>;
    }

    return (
      <Timeline>
        {history.map((entry) => (
          <Timeline.Item key={entry.id} dot={getChangeTypeIcon(entry.change_type)}>
            <div>
              <Text strong>{getChangeTypeLabel(entry.change_type)}</Text>
              {entry.field_changed && (
                <div>
                  <Text type="secondary">{entry.field_changed}: </Text>
                  <Text delete>{entry.old_value}</Text>
                  <Text> → </Text>
                  <Text strong>{entry.new_value}</Text>
                </div>
              )}
              {entry.reason && (
                <div>
                  <Text type="secondary">Reason: </Text>
                  <Text>{entry.reason}</Text>
                </div>
              )}
              <div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {dayjs(entry.created_at).format('MMM DD, YYYY @ h:mm A')} • {entry.changed_by_name}
                  {entry.customer_notified && ' • Customer notified'}
                </Text>
              </div>
            </div>
          </Timeline.Item>
        ))}
      </Timeline>
    );
  };

  const appt = fullAppointment || appointment;

  if (!appt) return null;

  // SP-1 Task 10: build a maps destination for the mechanic's "Navigate"
  // link. Prefer lat/lng (more precise turn-by-turn), else fall back to the
  // best available address string. Google's dir API accepts either.
  const addressParts = [
    appt.customer?.address,
    appt.customer?.city,
    appt.customer?.state,
    appt.customer?.zip,
  ].filter(Boolean);
  const addressDisplay = appt.location_address || addressParts.join(', ');
  const hasLatLng = appt.location_lat != null && appt.location_lng != null;
  const navigateDestination = hasLatLng
    ? `${appt.location_lat},${appt.location_lng}`
    : addressDisplay;
  const hasAddress = hasLatLng || !!addressDisplay;
  const mapsUrl = hasAddress
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(navigateDestination)}`
    : '';

  const canEdit = appt.status !== 'cancelled' && appt.status !== 'completed';

  const titleContent = (
    <Space>
      <CalendarOutlined />
      <span>Appointment Details</span>
    </Space>
  );

  const actionButtons = (
    <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
      {canEdit && (
        <>
          {onReschedule && (
            <Button
              icon={<CalendarOutlined />}
              onClick={() => onReschedule(appt)}
              block={isMobile}
              size={isMobile ? 'large' : 'middle'}
            >
              Reschedule
            </Button>
          )}
          {onChangeTechnician && (
            <Button
              icon={<SwapOutlined />}
              onClick={() => onChangeTechnician(appt)}
              block={isMobile}
              size={isMobile ? 'large' : 'middle'}
            >
              Change Technician
            </Button>
          )}
          {onCancel && (
            <Button
              danger
              icon={<StopOutlined />}
              onClick={() => onCancel(appt)}
              block={isMobile}
              size={isMobile ? 'large' : 'middle'}
            >
              Cancel Appointment
            </Button>
          )}
          {onDelete && (
            <Tooltip title="Permanently delete this appointment">
              <Button
                danger
                type={isMobile ? 'primary' : 'text'}
                icon={<DeleteOutlined />}
                onClick={() => onDelete(appt)}
                block={isMobile}
                size={isMobile ? 'large' : 'middle'}
              >
                Delete
              </Button>
            </Tooltip>
          )}
        </>
      )}
      <Button onClick={onClose} block={isMobile} size={isMobile ? 'large' : 'middle'}>
        Close
      </Button>
    </Space>
  );

  const content = (
      <Spin spinning={loading}>
        {/* Status Alert */}
        {appt.status === 'cancelled' && (
          <Alert
            message="This appointment has been cancelled"
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {appt.has_conflict && (
          <Alert
            message="This appointment has scheduling conflicts"
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Appointment Information */}
        <Card title="Information" size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={isMobile ? 1 : 2} size="small">
            <Descriptions.Item label="Title" span={isMobile ? 1 : 2}>
              <Text strong>{appt.title}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={jobStatusColor(appt).color}>{jobStatusColor(appt).label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag>{appt.appointment_type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              <Space>
                <ClockCircleOutlined />
                {dayjs(appt.start_time).format('MMMM DD, YYYY')}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Time">
              {dayjs(appt.start_time).format('h:mm A')} - {dayjs(appt.end_time).format('h:mm A')}
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
              {appt.duration_minutes} minutes
            </Descriptions.Item>
            {appt.description && (
              <Descriptions.Item label="Description" span={isMobile ? 1 : 2}>
                {appt.description}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Customer & Vehicle Information */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12}>
            <Card title={<><UserOutlined /> Customer</>} size="small" style={{ marginBottom: isMobile ? 16 : 0 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Name">
                  {appt.customer ?
                    (appt.customer.company_name || `${appt.customer.first_name} ${appt.customer.last_name}`)
                    : 'N/A'}
                </Descriptions.Item>
                {appt.customer?.email && (
                  <Descriptions.Item label="Email">
                    {appt.customer.email}
                  </Descriptions.Item>
                )}
                {appt.customer?.phone && (
                  <Descriptions.Item label="Phone">
                    {appt.customer.phone}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Address">
                  {hasAddress ? (
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Text>{addressDisplay}</Text>
                      <Button
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        icon={<EnvironmentOutlined />}
                        size="small"
                      >
                        Navigate
                      </Button>
                    </Space>
                  ) : (
                    <Text type="secondary">No address on file</Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card title={<><CarOutlined /> Vehicle</>} size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Vehicle">
                  {appt.vehicle ?
                    `${appt.vehicle.year} ${appt.vehicle.make} ${appt.vehicle.model}`
                    : 'N/A'}
                </Descriptions.Item>
                {appt.vehicle?.vin && (
                  <Descriptions.Item label="VIN">
                    {appt.vehicle.vin}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </Col>
        </Row>

        {/* Technician Information */}
        <Card title={<><UserOutlined /> Technician</>} size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Assigned To">
              {appt.technician_name || 'Unknown'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Notes */}
        {(appt.notes || appt.internal_notes) && (
          <Card title="Notes" size="small" style={{ marginBottom: 16 }}>
            {appt.notes && (
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Customer Notes:</Text>
                <div>{appt.notes}</div>
              </div>
            )}
            {appt.internal_notes && (
              <div>
                <Text type="secondary">Internal Notes:</Text>
                <div>{appt.internal_notes}</div>
              </div>
            )}
          </Card>
        )}

        {/* History */}
        <Card
          title={<><HistoryOutlined /> Change History</>}
          size="small"
        >
          {renderHistory()}
        </Card>

        {/* Action Buttons on Mobile (in content for better scrolling) */}
        {isMobile && (
          <div style={{ marginTop: 24, paddingBottom: 16 }}>
            {actionButtons}
          </div>
        )}
      </Spin>
  );

  // Render as Drawer on mobile, Modal on desktop
  if (isMobile) {
    return (
      <Drawer
        title={titleContent}
        placement="bottom"
        height="90%"
        open={visible}
        onClose={onClose}
        styles={{ body: { paddingBottom: 80 } }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      title={titleContent}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={actionButtons}
    >
      {content}
    </Modal>
  );
};
